import React from 'react';
import { Dispatch } from 'redux';
import { connect } from "react-redux";
import { IApplicationState, RootActionCreators } from '../../../store';
import { createStyles, makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from "react-router";

import { IReportDataObject, IReportData } from '../../../entities/reports/IReportData';
import { IMedewerkerList } from '../../../store/selectie';
import { IData as IOutreachDetail } from "../../../store/outreach/OutreachItem";
import { IData as  IOutreachDoelgroep } from "../../../store/outreach/OutreachDoelgroepen";
import { IData as IOutreachSetting } from "../../../store/outreach/outreachSettings";
import { IData as IOutreachItem } from "../../../store/outreach/OutreachItem";
import { FetchState } from '../../../entities/FetchState';
import { INaviagtions, mapNavigationsToProps, OutreachNavigation } from '../../organisms/outreach/OutreachNavigation';
import { PageHeader } from '../../organisms/PageHeader';
import { OutreachForm } from '../../organisms/outreach/OutreachForm';
import { ListButton } from '../../atoms/buttons/ListButton';
import { SaveButton } from '../../atoms/buttons/SaveButton';
import { DeleteButton } from '../../atoms/buttons/DeleteButton';
import { RefreshButton } from '../../atoms/buttons/RefreshButton';
import ErrorMessage from '../../organisms/ErrorMessage';
import { SavingMessage } from '../../organisms/SavingMessage';
import { DeletingMessage } from '../../organisms/DeletingMessage';
import { UnsavedChangesBar } from '../../organisms/UnsavedChangesBar';



// CSS
const useStyles = makeStyles((theme: Theme) =>
    createStyles({}),
  ); 

// Interface for Route Parameters
interface IMatchParams {
    id: string;
}
interface IPropsFromParams extends RouteComponentProps<IMatchParams> { }

// Standard Properties from parent component
interface IProps { }

// Outgoing functions to parent component
interface IDispatch {}

// Properties from State 
interface IStateProps { 
    
    
    details: IReportDataObject<IOutreachDetail>
    updater: IReportDataObject<IOutreachDetail>
    deleter: IReportDataObject<IOutreachDetail>
    doelgroepen: IReportData<IOutreachDoelgroep>
    settings: IReportDataObject<IOutreachSetting>
    medewerkes: IMedewerkerList
}
const mapStateProps = (state: IApplicationState): IStateProps => ({
    
    
    details: state.outreach.outreachItem.Item,
    doelgroepen: state.outreach.outreachDoelgroepen.Items,
    settings: state.outreach.outreachSettings.Item,
    medewerkes: state.selectie.allMedewerkersNoAll,
    updater: state.outreach.outreachItem.UpdateItem,
    deleter: state.outreach.outreachItem.DeleteItem
});

// Outgoing functions to Dispatch
interface IDispatchProps { 
    naviagations: INaviagtions;
    onLoadDetail: (id: string) => void;
    onLoadDoelgroepen: () => void;
    onLoadSettings: () => void;
    onLoadMedewerkers: () => void;
    onReturnList: () => void;
    onEdit: (details: IOutreachItem ) => void;
    onDelete: (id: string) => void;
}
const mapDispatchProps = (dispatch: Dispatch): IDispatchProps => ({
    naviagations: mapNavigationsToProps(dispatch),

    onLoadDetail: (id: string) => dispatch(RootActionCreators.Outreach.outreachItem.FetchData(id)),
    onLoadDoelgroepen: () => dispatch(RootActionCreators.Outreach.outreachDoelgroepen.FetchData()),
    onLoadSettings: () => dispatch(RootActionCreators.Outreach.outreachSettings.FetchData()),
    onLoadMedewerkers: () => dispatch(RootActionCreators.Selectie.FetchAllMedewerkers()),
    onReturnList: () => dispatch(RootActionCreators.Navigate.ToOutreach()),
    onEdit: (details: IOutreachItem) => dispatch(RootActionCreators.Outreach.outreachItem.UpdateData(details)),
    onDelete: (id: string) => dispatch(RootActionCreators.Outreach.outreachItem.DeleteData(id))
});

// Actual Component
export type AllProps = IProps & IDispatch & IStateProps & IDispatchProps & IPropsFromParams;
export const OutreachEdit = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tOutreach } = useTranslation('outreach');
    const { t: tCommon } = useTranslation('common');

    const isError = props.details.state === FetchState.Error || props.doelgroepen.state === FetchState.Error ||
                     props.settings.state === FetchState.Error || props.medewerkes.fetchState === FetchState.Error;

    const isLoading = props.details.state === FetchState.NotFetched || props.details.state === FetchState.Busy ||
                      props.doelgroepen.state === FetchState.NotFetched || props.doelgroepen.state === FetchState.Busy ||
                      props.settings.state === FetchState.NotFetched || props.settings.state === FetchState.Busy ||
                      props.medewerkes.fetchState === FetchState.NotFetched || props.medewerkes.fetchState === FetchState.Busy ||
                      isError;

    const [details, setDetails] = React.useState(props.details.data);
    const [detailsValid, setDetailsValid] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    
    // Load data when component mounts
    React.useEffect(() => {
        const id = props.match.params.id;
        if (props.details.state === FetchState.NotFetched) {
                setDetailsValid(false);
                props.onLoadDetail(id);
        } else if (props.details.state === FetchState.Success) {
            if (props.details.data.id !== id) {
                setDetailsValid(false);               
                props.onLoadDetail(id);
            }
        }
        if (props.details.state === FetchState.Success && props.details.data) {
            setDetailsValid(true);
            setDetails(props.details.data);
        }
    }, [props.details.state, props.match.params.id, props.details?.data.id]);


    // Load Doelgroepen when component mounts
    React.useEffect(() => {
        if (props.doelgroepen.state === FetchState.NotFetched) {
            props.onLoadDoelgroepen();
        }
    }, [props.doelgroepen.state]);

    // Load Settings when component mounts
    React.useEffect(() => {
        if (props.settings.state === FetchState.NotFetched) {
            props.onLoadSettings();
        }
    }, [props.settings.state]);
    
    // Load Medewerkers when component mounts
    React.useEffect(() => {
        if (props.medewerkes.fetchState === FetchState.NotFetched) {
            props.onLoadMedewerkers();
        }
    }, [props.medewerkes.fetchState]);

    // Reset unsaved changes flag after successful save
    React.useEffect(() => {
        if (props.updater.state === FetchState.Success) {
            setHasUnsavedChanges(false);
        }
    }, [props.updater.state]);

    const locked = details.locked || false;
    const errorMessages = [
        props.details.errorMessage,
        props.doelgroepen.errorMessage,
        props.settings.errorMessage,
        props.medewerkes.errorMessage
    ].join(', ');

    return (<OutreachNavigation navigations={props.naviagations}>

        <UnsavedChangesBar show={hasUnsavedChanges} />

        <PageHeader
                title="Outreach"
                subTitle="Outreach Edit"
                explainText="Hier kan je outreach gegevens aanpassen."
            />

        <OutreachForm
            data={details}
            doelgroepen={props.doelgroepen.data}
            loading={isLoading}
            minDate={props.settings.data.minDate}
            maxDate={props.settings.data.maxDate}
            minContacts={props.settings.data.minContacts}
            maxContacts={props.settings.data.maxContacts}
            onUpdate={(details, valid) => {
                setDetails(details);
                setDetailsValid(valid);
            }}
            onDirtyChange={() => setHasUnsavedChanges(true)}
        />

        <RefreshButton onClick={() => props.onLoadDetail(details.id)} disabled={props.details.state === FetchState.Busy} />
        <SaveButton disabled={locked || (detailsValid === false) || isLoading} onClick={() => props.onEdit(details)} />
        <DeleteButton disabled={locked || isLoading} onClick={() => props.onDelete(details.id)} deleteWarningMessage={tOutreach('outreachDeleteWarning', {...props.details.data, datum: props.details.data.datum.toLocaleDateString()})} />


        {isError && <ErrorMessage message={tCommon('errorLoadingData')} title={tCommon('errorLoadingDataTitle')} errorDetails={errorMessages} options={[{optionLabel: tCommon('restart'), optionExecute: () => window.location.reload()}]} />}

        {props.updater.state === FetchState.Busy && ! isError && <SavingMessage entity={tOutreach('outreach')} />}
        {props.updater.state === FetchState.Error && ! isError && <ErrorMessage title={tCommon('savingErrorTitle')} message={tCommon('savingErrorXMessage', {x: props.updater.errorMessage})} canClose options={[]} errorDetails={JSON.stringify(details)} />}

        {props.deleter.state === FetchState.Busy && ! isError && <DeletingMessage entity={tOutreach('outreach')} />}
        {props.deleter.state === FetchState.Error && ! isError && <ErrorMessage title={tCommon('deletingErrorTitle')} message={tCommon('deletingErrorXMessage', {x: props.updater.errorMessage})} canClose options={[]} errorDetails={JSON.stringify(details)} />}
    </OutreachNavigation>);
};

export default connect<IStateProps, IDispatchProps, {}, IApplicationState>(mapStateProps, mapDispatchProps)(OutreachEdit);